import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const siteUrl = "https://brw-fl.com";
const siteName = "BRW Contracting";
const defaultImage = `${siteUrl}/images/Image42222.webp`;

const defaultMeta = {
  title: "BRW Contracting | Site Development Contractor in Central Florida",
  description:
    "BRW Contracting provides site development, earthwork, underground utilities, concrete, paving, striping, and roadway construction across Central Florida.",
};

const pageMeta = {
  "/": defaultMeta,
  "/about": {
    title: "About BRW Contracting | Central Florida Site Development",
    description:
      "Learn about BRW Contracting, a full-service site development company serving Central Florida with dependable construction execution.",
  },
  "/services": {
    title: "Construction Services | BRW Contracting",
    description:
      "Explore BRW Contracting services including earthwork, underground utilities, paving, striping, concrete, roadway construction, and retaining walls.",
  },
  "/projects": {
    title: "Projects | BRW Contracting",
    description:
      "View completed BRW Contracting site development, roadway, utility, earthwork, paving, and concrete projects across Central Florida.",
  },
  "/team": {
    title: "Our Team | BRW Contracting",
    description:
      "Meet the BRW Contracting leadership and team members supporting construction projects throughout Central Florida.",
  },
  "/careers": {
    title: "Careers | BRW Contracting",
    description:
      "Join BRW Contracting field, equipment, office, and project teams working on construction projects across Central Florida.",
  },
  "/Join": {
    title: "Apply To Join BRW Contracting | Careers",
    description:
      "Apply for current BRW Contracting openings and send your role interest, work history, and contact information to our team.",
  },
  "/contact": {
    title: "Contact BRW Contracting | Land O' Lakes, FL",
    description:
      "Contact BRW Contracting for bid requests, project questions, careers, and office information in Land O' Lakes, Florida.",
  },
  "/concrete": {
    title: "Concrete Services | BRW Contracting",
    description:
      "BRW Contracting provides concrete services for commercial, roadway, and site development projects across Central Florida.",
  },
  "/earthwork": {
    title: "Earthwork Services | BRW Contracting",
    description:
      "BRW Contracting handles clearing, grading, excavation, and earthwork for site development projects in Central Florida.",
  },
  "/pavingStriping": {
    title: "Paving & Striping | BRW Contracting",
    description:
      "BRW Contracting provides asphalt paving, parking lot striping, and roadway finishes for commercial and site development projects.",
  },
  "/roadwayConstruction": {
    title: "Roadway Construction | BRW Contracting",
    description:
      "BRW Contracting builds roadway infrastructure with dependable crews, equipment, and field execution across Central Florida.",
  },
  "/SegmentedRetainingWalls": {
    title: "Segmented Retaining Walls | BRW Contracting",
    description:
      "BRW Contracting builds segmented retaining walls for site development, grading, and infrastructure projects.",
  },
  "/undergroundUtilities": {
    title: "Underground Utilities | BRW Contracting",
    description:
      "BRW Contracting installs underground utility infrastructure for commercial, municipal, and site development projects.",
  },
};

const projectPaths = [
  "/BoarsHeadRanch",
  "/DollarTreePhase1",
  "/DollarTreePhase2",
  "/OakCreekParkway",
  "/Longbayou",
  "/ParcWesleyChapel",
  "/BestBuyPolkCity",
  "/CenterstateLogistics",
  "/EppersonCommercial",
  "/Sr50",
  "/Sr52",
  "/HernandoLandfill",
  "/LexingtonCommons",
  "/LexingtonApartments",
  "/EppersonA1A5",
  "/SovereignTownCenter",
  "/Volaris",
  "/CostcoClermont",
  "/ZephyrCourt",
  "/SunlakeBusinessCenter",
  "/NorthTampa75BusinessCenter",
  "/SumterCountyBushnellUtility",
  "/AdventHealthWinterHaven",
  "/AngelinePhase4",
  "/PosnerBusinessCenter",
  "/CityOfZephyrhillsSidewalk",
  "/DairyRoadPhase2",
  "/RestaurantDepotHainesCity",
  "/LargoIndustrialBuilding1",
  "/tampaCommerce",
  "/harneyRd",
  "/simonsRd",
  "/tyndallRd",
  "/miradaBlvdPhase4",
  "/miradaRoundabout",
  "/kentonRd",
  "/curlyRdSegmentB",
  "/AuburndaleWarehouse",
  "/zephyrhillsImprovements",
  "/angelineGrandLiveOak",
  "/AdventHealthNorthDaleMabry",
  "/HerculesPark",
  "/LakeHideaway",
  "/OldPascoRd",
  "/MiradaParcelPhase1_2",
  "/RoyalHighlands",
  "/PowellRoad",
  "/HighlandTrails",
  "/HcaParrish",
  "/CouncilBayApolloBeach",
  "/BjsBrooksville",
  "/SienaCove",
  "/KohlsLutz",
  "/DobbsEquipment",
  "/OurLadyCemetery",
  "/LeoCottages",
  "/AcademyAtTheFarms",
  "/SVBTennisCenter",
  "/SunriseBlvd",
  "/CyrilDrive",
  "/NestHockey",
  "/AcademyAtTheLakes",
  "/PublixWaterset",
  "/LBAGandy",
  "/ApexLakeland",
  "/SR52CurleyRd",
  "/CityFurnitureWesleyChapel",
];

const projectTitleFromPath = (path) =>
  path
    .replace(/^\/+/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\bSr\b/g, "SR")
    .replace(/\bHca\b/g, "HCA")
    .replace(/\bBjs\b/g, "BJ's")
    .trim();

const setMetaTag = (selector, attribute, value) => {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    const match = selector.match(/meta\[(name|property)="([^"]+)"\]/);

    if (match) {
      tag.setAttribute(match[1], match[2]);
    }

    document.head.appendChild(tag);
  }

  tag.setAttribute(attribute, value);
};

const setCanonical = (href) => {
  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", href);
};

function Seo() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const fallbackTitle = projectTitleFromPath(path);
    const isProjectPage = projectPaths.includes(path);
    const isIndexablePage = Boolean(pageMeta[path]) || isProjectPage;
    const meta =
      pageMeta[path] ||
      (isProjectPage
        ? {
            title: `${fallbackTitle} | BRW Contracting Project`,
            description:
              "Explore this BRW Contracting project and learn more about our Central Florida site development and construction work.",
          }
        : {
            title: "Page Not Found | BRW Contracting",
            description:
              "The requested BRW Contracting page could not be found. Visit our services, projects, careers, or contact pages.",
          });
    const canonicalUrl = `${siteUrl}${path === "/" ? "/" : path}`;

    document.title = meta.title;
    document.documentElement.lang = "en";

    setMetaTag('meta[name="description"]', "content", meta.description);
    setMetaTag(
      'meta[name="robots"]',
      "content",
      isIndexablePage ? "index, follow" : "noindex, follow"
    );
    setMetaTag('meta[property="og:title"]', "content", meta.title);
    setMetaTag('meta[property="og:description"]', "content", meta.description);
    setMetaTag('meta[property="og:url"]', "content", canonicalUrl);
    setMetaTag('meta[property="og:type"]', "content", "website");
    setMetaTag('meta[property="og:site_name"]', "content", siteName);
    setMetaTag('meta[property="og:image"]', "content", defaultImage);
    setMetaTag('meta[name="twitter:card"]', "content", "summary_large_image");
    setMetaTag('meta[name="twitter:title"]', "content", meta.title);
    setMetaTag('meta[name="twitter:description"]', "content", meta.description);
    setMetaTag('meta[name="twitter:image"]', "content", defaultImage);
    setCanonical(canonicalUrl);
  }, [location.pathname]);

  return null;
}

export default Seo;
